// ANA-R1-01 (AC3) — map a defect-mutation failure to the error snackbar shape.
//
// The walkthrough's defect-delete toast said "update failed error id unknown"
// because the toast read `err.errorId` and fell back to the literal
// "unknown". The api-client normalises every rejection into an `AeroApiError`
// whose `errorId` now resolves through the lyse/v1 envelope (errorId →
// requestId → `code`, e.g. `not_found` for a 404), and whose `message` carries
// the BE's human text. This helper lifts BOTH onto the snack so any remaining
// failure path shows a REAL id + message — never the bare "unknown".
import type { AeroApiError } from "@aeriosiq/api-client";

export interface ErrorSnack {
  kind: "error";
  errorId: string;
  message?: string;
}

/**
 * Build the error-snack payload from a mutation error. Accepts the loose
 * `Error & { errorId?: string }` the mutation hooks declare; reads the
 * normalised `errorId` + `message` the api-client populates. Falls back to
 * "unknown" ONLY when no id is present at all (e.g. a non-HTTP failure with no
 * envelope) — a real BE error (incl. 404 `not_found`) never reaches that.
 */
export function mutationErrorToSnack(
  err: (Error & { errorId?: string; code?: string }) | AeroApiError,
): ErrorSnack {
  const errorId =
    err.errorId && err.errorId.length > 0
      ? err.errorId
      : (err.code ?? "unknown");
  const message =
    typeof err.message === "string" && err.message.trim().length > 0
      ? err.message
      : undefined;
  return message !== undefined
    ? { kind: "error", errorId, message }
    : { kind: "error", errorId };
}
